import { normalizeLabel } from '@features/event-level-column-rename/constants';
import type {
  CanonicalColumnId,
  ColumnLabelResolver,
} from '@features/event-level-column-rename/types';

/**
 * Line-item export sheet column key (export builder's identifiers) to canonical id. Watch the
 * near-misses: `quantity` is the items-per-unit column, not quantityOfUnits, and
 * `extendedQuantity` is deliberately unmapped — its constant and its header disagree about which
 * column it is, and it is stripped from every export anyway. Extend only with a verified pairing:
 * a guessed entry compiles, passes its own tests and renames the wrong column.
 */
export const BUYER_EXPORT_COLUMN_CANONICAL_IDS = {
  discount: 'discountPerUnit',
  name: 'lineItemName',
  negotiationPricePerUnit: 'negotiationPricePerUnit',
  pricePerUnit: 'pricePerUnit',
  quantity: 'itemsPerUnit',
  quantityOfUnits: 'quantityOfUnits',
  unitOfMeasure: 'unitOfMeasure',
} as const satisfies Record<string, CanonicalColumnId>;

type BuyerExportColumnKey = keyof typeof BUYER_EXPORT_COLUMN_CANONICAL_IDS;

/**
 * Repaints the export's header map as the final step before the header row is written.
 *
 * Keyed on export column keys — never on header text — so the repaint cannot bind to the wrong
 * column however the labels read. Columns without a canonical id, and columns whose event map
 * carries no override, keep the label the surface prints today (the resolver's fallback), so
 * per-surface defaults like "Baseline Price per Unit" survive untouched.
 *
 * `headers` is the full default-header map, built before optional columns (price, discount,
 * logistics charge, …) are stripped for this specific export — pass `exportedColumnKeys` (the
 * builder's own post-strip column list) so a rename is never rejected as colliding with a column
 * that will not actually appear in this sheet.
 *
 * An override whose normalised label collides with another mapped column's header or resolved
 * label is not applied — the sheet would ship two identical headers, which the upload side
 * refuses to auto-match — so the colliding column keeps its surface wording and both halves
 * stay on the same rule. The write path rejects such labels against canonical defaults, but a
 * per-surface wording can still collide with a legitimately stored label.
 *
 * Resolves each header exactly once per export, never per row — the export path is
 * load-sensitive.
 */
export const repaintBuyerExportHeaders = (
  headers: Record<string, string>,
  labelFor: ColumnLabelResolver,
  exportedColumnKeys?: readonly string[]
): Record<string, string> => {
  const repainted = { ...headers };

  const exportColumnKeys = (Object.keys(BUYER_EXPORT_COLUMN_CANONICAL_IDS) as BuyerExportColumnKey[])
    .filter(exportColumnKey => exportColumnKey in headers)
    .filter(exportColumnKey => !exportedColumnKeys || exportedColumnKeys.includes(exportColumnKey));

  const resolvedHeaders = new Map<BuyerExportColumnKey, string>(exportColumnKeys.map(exportColumnKey => [
    exportColumnKey,
    labelFor(BUYER_EXPORT_COLUMN_CANONICAL_IDS[exportColumnKey], headers[exportColumnKey]),
  ]));

  const collides = (exportColumnKey: BuyerExportColumnKey, label: string): boolean =>
    exportColumnKeys.some(otherKey => {
      if (otherKey === exportColumnKey) return false;

      const otherNames = [resolvedHeaders.get(otherKey) as string, headers[otherKey]];
      return otherNames.some(otherName => normalizeLabel(otherName) === normalizeLabel(label));
    });

  exportColumnKeys.forEach(exportColumnKey => {
    const resolvedHeader = resolvedHeaders.get(exportColumnKey) as string;

    if (resolvedHeader === headers[exportColumnKey]) return;
    if (collides(exportColumnKey, resolvedHeader)) return;

    repainted[exportColumnKey] = resolvedHeader;
  });

  return repainted;
};
